import { useEffect, useState, useRef } from 'react'
import { loadGoogleMaps } from './googleMapsLoader'

/**
 * @param {string[]} addresses
 * @returns {{loading:boolean, locations:{lat:number,lng:number}[]}}
 */
export function useGeocodedLocations(addresses = []) {
  const [state, setState] = useState({ loading: true, locations: [] })
  const alive = useRef(true)

  useEffect(() => {
    alive.current = true
    setState({ loading: true, locations: [] })

    async function run() {
      if (!alive.current) { return } // component unmounted → abort

      try {
        await loadGoogleMaps() // Ensure API is loaded
      } catch (e) {
        console.warn('Failed to load Google Maps API for geocoding:', e)
        if (alive.current) { setState({ loading: false, locations: [] }) }
        return
      }

      // Poll for the Geocoder to be ready.
      let geocoder
      const maxRetries = 20 // Poll for up to 2 seconds.
      for (let i = 0; i < maxRetries; i++) {
        if (window.google?.maps?.Geocoder) {
          geocoder = new window.google.maps.Geocoder()
          break
        }
        await new Promise((resolve) => setTimeout(resolve, 100))
      }

      if (!geocoder) {
        console.error('Google Maps loaded, but Geocoder is missing.')
        if (alive.current) { setState({ loading: false, locations: [] }) }
        return
      }

      const results = []

      for (const addr of addresses) {
        if (!addr) { continue } // Skip empty addresses
        try {
          /* eslint-disable no-await-in-loop */
          const loc = await new Promise((resolve, reject) => {
            geocoder.geocode({ address: addr }, (r, s) => {
              if (!alive.current) { return reject(new Error('Component unmounted')) } // component unmounted → abort
              s === 'OK' && r[0] ? resolve(r[0].geometry.location) : reject(s)
            })
          })
          if (!alive.current) { return } // component unmounted → abort
          results.push({ lat: loc.lat(), lng: loc.lng() })
        } catch (e) {
          console.warn('Geocode failed →', addr, e)
        }
      }

      if (alive.current) { setState({ loading: false, locations: results }) }
    }
    run()

    return () => { alive.current = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addresses.join('|')]) // Stable dependency array

  return state
}
